/**
 * Operator-facing session player. Reached via /play/<sid>.
 *
 * Mounts `SyncedViewerPanel` against a synthesised `BufferedSession`
 * so the operator gets gapless playback + map / IMU / speed sync +
 * live-mode appends for any V2 phone OR V1 box session. The route is
 * minimal because the synced viewer already does the heavy lifting —
 * we just hand it a `session` prop in the shape it expects.
 *
 * Why synthesise: the per-session buffered lookup returns the
 * canonical `BufferedSession` for any session with at least one v1
 * segment in S3. `/api/fleet/sessions/<sid>` then gives us
 * authoritative `state` (recording / ended / unclean) so we can
 * override `is_active` correctly even if the buffered answer is
 * stale.
 *
 * A media-contract-v2 session has NO buffered descriptor at all — its
 * objects are content-addressed under a per-rendition prefix that the
 * buffered walk's `sessions/<sid>/<cam>/segment_N.<ext>` pattern cannot
 * match, so that endpoint answers null however much media has landed.
 * The coverage endpoint is the contract-version authority (it serves
 * both contracts), so a null descriptor is probed there before it is
 * reported as "nothing uploaded", and once a session proves v2 the
 * buffered probe is skipped on later polls (`isV2Ref`). FeedPlayer
 * probes the other way around (coverage first); this page keeps its
 * bounded single-session buffered probe first because an operator URL
 * is as likely to name a v1 box session as a v2 phone.
 *
 * App.tsx matches the route manually and passes `sessionId` as a prop
 * — same pattern as `ShareViewerPage`.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Maximize, Minimize } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../services/api';
import { BufferedSession, FleetSessionMeta, SessionCoverage } from '../types';
import PluginSlot from '../components/PluginSlot';
import SessionTrimClipCard from '../components/SessionTrimClipCard';
import UploadTelemetryPanel from '../components/UploadTelemetryPanel';

interface PlaySessionPageProps {
  sessionId: string;
}

const v2MasterUrl = (sessionId: string) =>
  `/api/fleet/sessions/${encodeURIComponent(sessionId)}/v2/master.m3u8`;

/**
 * The `BufferedSession` shape the synced-viewer plugin expects, for a session
 * that has no buffered descriptor. `cameras` stays EMPTY on purpose: a v2
 * session has no per-camera buffered rendition to describe, and the fields of
 * `BufferedCamera` (playlist_url, segment_count, buffer_percent) have no v2
 * counterpart — inventing them would feed legacy-only consumers plausible
 * nonsense. The v2 player is mounted from `ctx.v2` instead.
 */
const synthesiseV2Session = (
  sessionId: string,
  meta: FleetSessionMeta,
  coverage: SessionCoverage,
): BufferedSession => {
  const union = coverage.playable_union ?? coverage.union ?? [];
  const lastEndMs = union.length ? union[union.length - 1][1] : null;
  return {
    session_id: sessionId,
    device_id: meta.device_id ?? null,
    master_url: v2MasterUrl(sessionId),
    cameras: [],
    started_at: meta.started_at,
    last_update: lastEndMs === null ? null : new Date(lastEndMs).toISOString(),
    is_active: meta.state === 'recording',
    is_buffer_ready: true,
    buffer_segments_required: 0,
  };
};

export const PlaySessionPage: React.FC<PlaySessionPageProps> = ({ sessionId }) => {
  const [session, setSession] = useState<BufferedSession | null>(null);
  const [meta, setMeta] = useState<FleetSessionMeta | null>(null);
  // Set only for media-contract-v2 sessions. Refreshed by the same 5 s poll as
  // the rest of the page: unions widen in place, and a snapshot_rev change
  // reloads the player's source in place too (see V2Player's snapshotRev prop).
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Playhead UTC mirrored out of the synced viewer plugin — anchors the
  // trim/clip card's "at playhead" actions.
  const [playheadUtcMs, setPlayheadUtcMs] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // A session's media contract never changes, so once coverage has answered
  // "v2" the buffered probe is skipped for the rest of the page's life — it
  // walks the session's whole S3 prefix to conclude "null" every poll.
  const isV2Ref = useRef(false);

  useEffect(() => {
    let cancelled = false;
    isV2Ref.current = false;

    const load = async () => {
      try {
        // Per-session buffered lookup instead of listBufferedSessions().find()
        // — the latter walked all of S3 (O(sessions × cameras)) just to find
        // one session, easily 30s+ under live-recording load. The new
        // /streams/buffered/<sid> endpoint does the same work for one
        // session in <200 ms.
        const [found, sessionMeta] = await Promise.all([
          isV2Ref.current ? null : api.getBufferedSession(sessionId).catch(() => null),
          api.getSessionMetadata(sessionId).catch(() => null),
        ]);
        if (cancelled) return;
        if (sessionMeta) setMeta(sessionMeta);
        if (!found) {
          if (!sessionMeta) {
            setError(`session ${sessionId} not found`);
            return;
          }
          const coverage = await api.getSessionCoverage(sessionId).catch(() => null);
          if (cancelled) return;
          if (coverage?.media_contract_version === 2) {
            isV2Ref.current = true;
            setV2Coverage(coverage);
            setSession(synthesiseV2Session(sessionId, sessionMeta, coverage));
            setError(null);
            return;
          }
          if (isV2Ref.current) {
            // A session's media contract is immutable, so only the FIRST
            // successful probe decides it. A later probe that fails, or that
            // answers "legacy" because the coverage route could not read the
            // session row and fell through, is a bad answer about a v2
            // session — not news that its media disappeared. Keep the player
            // on its stable master and let the next tick refresh the unions;
            // tearing down here reported a live session as "no segments
            // uploaded yet" mid-playback.
            return;
          }
          setV2Coverage(null);
          setSession(null);
          setError('no segments uploaded yet');
          return;
        }
        // Authoritative `is_active` comes from FleetSession.state —
        // overrides whatever buffered listing inferred from S3 timestamps.
        const isActive = sessionMeta
          ? sessionMeta.state === 'recording'
          : found.is_active;
        isV2Ref.current = false;
        setV2Coverage(null);
        setSession({ ...found, is_active: isActive });
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load session');
        }
      }
    };

    void load();
    const intervalId = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [sessionId]);

  // The v2 master can legitimately be unservable for a while — a session that
  // is recording but has not yet composed a timeline answers 409 until its
  // first timed artifact lands. Drop back to the loading/error state so the 5 s
  // poll re-probes and remounts the player once it becomes servable: a slow,
  // visible retry rather than a permanent overlay on a stuck element.
  const handleV2FatalError = (detail: string) => {
    isV2Ref.current = false;
    setV2Coverage(null);
    setSession(null);
    setError(`v2 playback unavailable — ${detail}`);
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('fullscreen failed', err);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-bg-base p-2 text-fg sm:p-4">
      <div className="mb-2 flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.history.length > 1) window.history.back();
                  else window.location.href = '/fleet';
                }}
                aria-label="Back to fleet"
              >
                <ArrowLeft />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Back to fleet</TooltipContent>
          </Tooltip>
          <span className="font-mono text-accent-purple">Map Player · {sessionId}</span>
          {meta?.state && (
            <Badge
              variant="outline"
              className={cn(
                'font-mono',
                meta.state === 'recording'
                  ? 'border-status-online text-status-online'
                  : 'border-border text-fg-secondary',
              )}
            >
              {meta.state}
            </Badge>
          )}
          {meta?.device_id && (
            <Badge variant="outline" className="font-mono text-fg-secondary">
              {meta.device_id}
            </Badge>
          )}
          <div className="flex-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize /> : <Maximize />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {error && !session && (
        <div className="mt-16 flex flex-col items-center gap-2">
          <p className="font-mono text-status-warning">{error}</p>
          {meta?.state === 'recording' && (
            <p className="text-xs text-fg-muted">
              recording is live; segments will appear here as they upload
            </p>
          )}
        </div>
      )}

      {!session && !error && (
        <div className="mt-16 flex flex-col items-center gap-3">
          <Spinner size={28} />
          <p className="text-fg-secondary">loading session…</p>
        </div>
      )}

      {session && (
        <div className="mt-2">
          <PluginSlot
            id="session.detail.panels"
            session={session}
            onUtcUpdate={setPlayheadUtcMs}
            v2={
              v2Coverage
                ? {
                    masterUrl: v2MasterUrl(sessionId),
                    audioUnion: v2Coverage.audio_union,
                    videoUnion: v2Coverage.video_union,
                    snapshotRev: v2Coverage.snapshot_rev ?? null,
                  }
                : undefined
            }
            onV2FatalError={handleV2FatalError}
          />
          {/* Trim (range-delete at 6 s segment granularity) + wrapped
              clip downloads (docs/SESSION_TRIM_AND_CLIPS.md). Operator
              page only — the backend routes are operator-authed too.
              Both operate on the legacy per-camera segment layout, so
              they are hidden for media-contract-v2 sessions rather than
              offered as controls that would delete nothing. */}
          {!v2Coverage && (
            <SessionTrimClipCard
              sessionId={sessionId}
              meta={meta}
              playheadUtcMs={playheadUtcMs}
              cameras={session.cameras.map((c) => c.camera_id)}
            />
          )}
          {/* Saved per-session upload-pipeline history (work-start →
              drain-complete). Renders a graceful empty state for sessions
              recorded before per-session attribution shipped. */}
          <div className="mt-3">
            <UploadTelemetryPanel sessionId={sessionId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaySessionPage;
